<template>
  <div class="workflow-states-progress">
    <div class="workflow-header">
      <span class="workflow-title">Filter by workflow state</span>
      <v-btn 
        x-small 
        text 
        color="primary" 
        @click="clearWorkflowFilter"
        class="clear-btn"
      >
        <v-icon x-small class="mr-1">mdi-close</v-icon>
        Clear
      </v-btn>
    </div>
    <div class="workflow-steps-container">
      <div class="workflow-responsive-container">
        <!-- Main workflow states -->
        <div class="workflow-main-steps">
          <template v-for="(state, index) in workflowStates">
            <div 
              :key="'step-' + state.id"
              class="workflow-step"
              :class="{ 
                'selected': isStateSelected(state.id),
                'has-count': getStateCount(state.id) > 0
              }"
              @click="onStateClick(state.id)"
            >
              <!-- Step badge -->
              <div class="step-badge" :class="getStepClass(state.id)">
                <span class="step-count">{{ getStateCount(state.id) }}</span>
              </div>
              
              <!-- Step label -->
              <div class="step-label">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{ state.name }}</span>
                  </template>
                  <span>{{ state.name }} ({{ getStateCount(state.id) }} portal{{ getStateCount(state.id) !== 1 ? 's' : '' }})</span>
                </v-tooltip>
              </div>
            </div>
            
            <!-- Arrow connector -->
            <div v-if="index < workflowStates.length - 1" :key="'arrow-' + state.id" class="workflow-connector">
              <svg width="20" height="12" viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 6 L14 6 M14 6 L11 3 M14 6 L11 9" stroke="#d0d5dd" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </svg>
            </div>
          </template>
        </div>
        
        <!-- Not Set section (separated) -->
        <div class="workflow-divider"></div>
        <div 
          class="workflow-step not-set-step" 
          :class="{ 
            'selected': isStateSelected('not-set'),
            'has-count': notSetCount > 0
          }" 
          @click="onNotSetClick()"
        >
          <div class="step-badge not-set" :class="{ 
            'has-items': notSetCount > 0,
            'selected': isStateSelected('not-set')
          }">
            <span class="step-count">{{ notSetCount }}</span>
          </div>
          <div class="step-label">Not Set</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WorkflowStatesProgress',
  props: {
    workflowStates: {
      type: Array,
      default: () => []
    },
    // Portal counts keyed by state id, plus 'not-set' (computed by the parent
    // so this component works with both legacy and multi-workflow data models)
    stateCounts: {
      type: Object,
      default: () => ({})
    },
    selectedStates: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    // Count of portals with no workflow state or orphaned workflow state
    notSetCount() {
      return this.stateCounts['not-set'] || 0;
    }
  },
  methods: {
    // Get count of portals for a specific workflow state
    getStateCount(stateId) {
      return this.stateCounts[stateId] || 0;
    },
    
    // Get CSS class for step circle based on count and selection
    getStepClass(stateId) {
      const count = this.getStateCount(stateId);
      const isSelected = this.isStateSelected(stateId);
      return {
        'has-items': count > 0,
        'empty': count === 0,
        'selected': isSelected
      };
    },
    
    // Check if a state is currently selected
    isStateSelected(stateId) {
      return this.selectedStates.includes(stateId);
    },
    
    // Handle click on workflow state
    onStateClick(stateId) {
      // Always emit click for selection/deselection, regardless of count
      this.$emit('state-clicked', stateId);
    },
    
    // Handle click on "Not Set"
    onNotSetClick() {
      // Always emit click for selection/deselection, regardless of count
      this.$emit('state-clicked', 'not-set');
    },
    
    // Clear workflow filter
    clearWorkflowFilter() {
      this.$emit('clear-filter');
    }
  }
}
</script>

<style scoped>
/* Professional Workflow States Design - Ultra Condensed */
.workflow-states-progress {
  background: #ffffff;
  border-radius: 8px;
  width: fit-content;
  max-width: 100%;
  padding: 10px 12px;
}

.workflow-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.workflow-title {
  font-size: 10px;
  font-weight: 600;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clear-btn {
  text-transform: none;
  font-size: 10px;
  height: 20px !important;
  min-width: 45px !important;
}

.workflow-steps-container {
  min-height: 28px;
}

.workflow-responsive-container {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 2px;
  padding: 1px 0;
}

.workflow-main-steps {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 1;
  min-width: 0;
}

.workflow-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8f9fa;
  border: 1px solid transparent;
  min-width: 60px;
  flex-shrink: 0;
}

.workflow-step:hover {
  background: #e8eaed;
  transform: translateY(-1px);
}

.workflow-step.selected {
  background: var(--brand-primary-color) !important;
  border-color: var(--brand-primary-color) !important;
  box-shadow: 0 3px 8px rgba(var(--brand-primary-color-rgb, 44, 90, 160), 0.3);
}

.workflow-step.has-count {
  background: #ffffff;
  border-color: #e8eaed;
}

.workflow-step.has-count:hover {
  border-color: var(--brand-primary-color);
  box-shadow: 0 2px 6px rgba(var(--brand-primary-color-rgb, 44, 90, 160), 0.12);
}

.step-badge {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e8eaed;
  color: #80868b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.step-badge.has-items {
  background: var(--brand-primary-color);
  color: #ffffff;
}

.step-badge.empty {
  background: #f8f9fa;
  color: #d0d5dd;
}

.workflow-step.selected .step-badge {
  background: #ffffff !important;
  color: var(--brand-primary-color) !important;
  border: 2px solid #ffffff;
  box-shadow: none;
  font-weight: 700;
}

.step-label {
  font-size: 9px;
  font-weight: 500;
  color: #5f6368;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.2;
}

.workflow-step.has-count .step-label {
  color: #1a1a1a;
  font-weight: 600;
}

.workflow-step.selected .step-label {
  color: #ffffff !important;
  font-weight: 700;
}

.workflow-connector {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin: 0 1px;
  padding-bottom: 14px;
}

.workflow-divider {
  width: 1px;
  height: 36px;
  background: repeating-linear-gradient(
    to bottom,
    #d0d5dd 0px,
    #d0d5dd 3px,
    transparent 3px,
    transparent 6px
  );
  flex-shrink: 0;
  margin: 0 6px;
}

.not-set-step {
  min-width: 55px;
}

.step-badge.not-set {
  background: #f8f9fa;
  color: #d0d5dd;
}

.step-badge.not-set.has-items {
  background: #ff9800;
  color: #ffffff;
}

.not-set-step.selected {
  background: var(--brand-primary-color) !important;
  
  .step-badge {
    background: #ffffff !important;
    color: var(--brand-primary-color) !important;
    border: 2px solid #ffffff;
    font-weight: 700;
  }
  
  .step-label {
    color: #ffffff !important;
    font-weight: 700;
  }
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .workflow-states-progress {
    padding: 10px;
  }
  
  .workflow-step {
    min-width: 55px;
    padding: 5px 8px;
  }
  
  .step-badge {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }
  
  .step-label {
    font-size: 9px;
  }
  
  .workflow-connector {
    margin: 0 1px;
  }
  
  .workflow-divider {
    margin: 0 6px;
  }
}

@media (max-width: 600px) {
  .workflow-responsive-container {
    gap: 3px;
  }
  
  .workflow-main-steps {
    gap: 3px;
  }
  
  .workflow-step {
    min-width: 48px;
    padding: 4px 6px;
    gap: 5px;
  }
  
  .step-badge {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }
  
  .step-label {
    font-size: 8px;
  }
  
  .workflow-divider {
    margin: 0 4px;
    height: 28px;
  }
}

.step-count {
  line-height: 1;
}
</style>
